<template lang="html">
  <item-editor-dialog
    :id="selectedItemId"
    ref="editForm"
    v-model="selectedPanel"
    :title="pageTitle"
    :on-close="doClose"
    :loading="isDataLoading"
    :show="showItem"
    :errors="errors"
    :edit-panel-id="editPanel"
  >
    <div class="instructions mb-3">
      <div class="instructions-title">
        Instructions
      </div>

      <div class="instructions-text">
        Review charges and confirm accuracy. Click “Send Charges to POS” to generate tickets.
        Click any guest to view or update their reservation.
      </div>
    </div>

    <v-container
      fluid
      class="px-6"
    >
      <v-row
        class="mb-3"
        dense
      >
        <v-col
          cols="12"
          sm="6"
        >
          <div class="field">
            <div class="field-label">
              {{ type }} Name
            </div>
            <div class="field-value">
              {{ itemData.name || '—' }}
            </div>
          </div>
        </v-col>

        <v-col
          cols="12"
          sm="6"
        >
          <div class="field">
            <div class="field-label">
              Comments
            </div>
            <div class="field-value">
              {{ itemData.notes || '—' }}
            </div>
          </div>
        </v-col>
      </v-row>

      <v-divider class="my-3" />

      <v-row class="eventCounts mb-4">
        <v-col
          v-for="card in summaryCards"
          :key="card.label"
          cols="12"
          sm="4"
        >
          <v-card class="pa-4 summary-card">
            <div class="text-caption text--secondary mb-1">
              {{ card.label }}
            </div>
            <div class="text-h6 font-weight-medium">
              {{ card.value }}
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-divider class="my-4" />

      <v-tabs
        v-model="activeTabIndex"
        class="mb-3"
      >
        <v-tab>
          Billable ({{ billableReservees }})
        </v-tab>

        <v-tab>
          Already Charged ({{ billedReservees }})
        </v-tab>
      </v-tabs>

      <v-divider class="mb-3" />

      <v-simple-table
        dense
        class="checks-table"
      >
        <thead>
          <tr>
            <th class="text-left">
              Reservation
            </th>
            <th class="text-left">
              Guest
            </th>
            <th class="text-left">
              Type
            </th>
            <th class="text-left">
              Status
            </th>
            <th class="text-left">
              Category
            </th>
            <th class="text-right">
              Price
            </th>
            <th class="text-left">
              Charge Code
            </th>
          </tr>
        </thead>

        <tbody>
          <template v-for="check in groupedChecks">
            <!-- GROUP HEADER -->
            <tr
              :key="check.key + '-header'"
              class="group-row"
              @click="gotoReservation(check)"
            >
              <td colspan="7">
                <div class="d-flex align-center group-header-content">
                  <div>
                    <span class="font-weight-medium resLink">
                      Reservation {{ check.reservationId }}
                    </span>
                    <span class="ml-2 text-caption text--secondary">
                      - Check {{ check.checkNumber || '—' }}
                    </span>
                  </div>

                  <div class="billed-to text-caption">
                    Billed to:
                    <span class="font-weight-medium">
                      {{
                        check.payer && check.payer.nameDisplay === check.payer.guestTypeName
                          ? 'Guest Account'
                          : (check.payer && check.payer.nameDisplay) || '—'
                      }}
                    </span>
                  </div>
                </div>
              </td>
            </tr>

            <!-- GUEST ROWS -->
            <tr
              v-for="guest in check.guests"
              :key="guest.id"
              class="guest-row"
            >
              <td />

              <td>
                {{ guest.nameDisplay }}
                <v-chip
                  v-if="guest.isPayer"
                  x-small
                  color="primary"
                  text-color="white"
                  class="ml-1"
                >
                  Bill To
                </v-chip>
              </td>

              <td class="text-caption">
                {{ guest.guestTypeDisplay }}
              </td>

              <td class="text-caption">
                {{ getStatusDisplay(guest) }}
              </td>

              <td class="text-caption">
                {{ guest.eventCostName }}
              </td>

              <td class="text-right font-weight-medium">
                {{ guest.eventCostPriceFormatted }}
              </td>

              <td class="text-caption">
                {{ guest.chargeCodeDisplay }}
              </td>
            </tr>
          </template>
        </tbody>
      </v-simple-table>

      <v-divider class="my-3" />

      <div
        v-if="activeTabIndex === 0"
        class="actions-bar px-4 py-3"
      >
        <div style="flex: 1" />

        <div class="text-caption mr-4">
          {{ billableReservees }} billable reservees
        </div>

        <v-btn
          class="send-charges-btn"
          color="primary"
          :loading="isSending"
          :disabled="isSending || !billableDetails.length"
          @click="confirmSendCharges"
        >
          Send Charges to POS
        </v-btn>
      </div>
    </v-container>
  </item-editor-dialog>
</template>

<script>
import {itemEdit, helpers} from '@/ft-vue-core'
import moment from 'moment'

const {forceCurrency} = helpers

const itemDefault = {
  id: null,
  name: null,
  date: null,
  covers: null,
  notes: null,
  details: []
}

export default {
  name: 'EventBillingItem',
  mixins: [itemEdit],

  data () {
    return {
      type: 'Event',
      types: 'Event',
      apiRecordCommand: 'diningEvent',
      reservations: [],
      itemDefault,
      skipRecordLock: true,
      search: '',
      isSending: false,

      activeTabIndex: 0,
    }
  },
  computed: {
    pageTitle () {
      return 'Process Event Billing'
    },

    summaryCards () {
      return [
        {label: 'Total Reservations', value: this.totalReservations},
        {label: 'Total Reservees', value: this.totalReservees},
        {label: 'Billable Reservees', value: this.billableReservees}
      ]
    },

    allDetails () {
      return (this.reservations || []).flatMap(
        r => r.details || []
      )
    },

    billableDetails () {
      const all = this.allDetails || []

      return all.filter(d =>
        this.isEligible(d) && d.billed == null
      )
    },

    billedDetails () {
      const all = this.allDetails || []

      return all.filter(d =>
        this.isEligible(d) && d.billed
      )
    },

    totalReservations () {
      return (this.reservations || []).length
    },

    totalReservees () {
      return (this.allDetails || []).length
    },

    billableReservees () {
      return this.billableDetails.length
    },

    billedReservees () {
      return this.billedDetails.length
    },

    groupedChecks () {
      const source =
          this.activeTabIndex === 1
            ? this.billedDetails
            : this.billableDetails

      const map = {}

      source.forEach(d => {
        const key = `${d.reservationId}-${d.checkNumber || 'none'}`

        if (!map[key]) {
          map[key] = {
            key,
            reservationId: d.reservationId,
            checkNumber: d.checkNumber,
            guests: []
          }
        }

        map[key].guests.push(this.toReserveeRow(d))
      })

      return Object.values(map).map(group => {
        const guests = group.guests.map(g => ({
          ...g
        }))

        const billToGuest =
          guests.find(g => g.billTo === true) || guests[0] || null

        const billToId = billToGuest?.id

        const normalizedGuests = guests.map(g => ({
          ...g,
          isPayer: g.id === billToId
        }))

        return {
          ...group,
          guests: normalizedGuests,
          payer: billToGuest
        }
      })
    }

  },

  methods: {
    getStatusDisplay (d) {
      const parts = []

      if (d.cancelled) {
        const time = this.formatDateTime(d.cancelled)

        parts.push(
          `Canceled${time ? ` (${time})` : ''}`
        )
      }

      if (d.show === false) {
        parts.push('No Show')
      }

      if (!parts.length) {
        return '—'
      }

      return parts.join(' • ')
    },
    toReserveeRow (d) {
      return {
        ...d,
        billTo: !!d.billTo,
        reservationId: d.reservationId,
        nameDisplay: this.buildNameDisplay(d),
        eventCostPriceFormatted: d.eventCostPrice != null
          ? `$${forceCurrency(d.eventCostPrice)}`
          : '',
        guestTypeDisplay: d.guestTypeName || 'Member',
        chargeCodeDisplay: this.getChargeCodeDisplay(d) || '—'
      }
    },
    getChargeCodeDisplay (d) {
      const cancellationCode = this.itemData?.cancellationChargeCode || ''

      if (this.isCancelled(d)) {
        return cancellationCode
      }

      if (d.show === false) {
        return cancellationCode
      }

      return d.eventCostChargeCode || '—'
    },
    isCancelled (d) {
      return d.cancelled != null
    },
    buildNameDisplay (d) {
      const name = d.name || d.guestTypeName
      const member = d.memberNumber

      if (!member) return name

      return `${name} — #${member}`
    },
    isEligible (d) {
      const hasCost = d.eventCostId !== null && d.eventCostChargeCode
      const notCancelled = !this.isCancelled(d)
      const feeAllowed = this.isCancelled(d) && !d.cancelFeeExempt

      return hasCost && (notCancelled || feeAllowed)
    },

    formatDateTime (datetime) {
      return datetime
        ? moment(datetime).format('YYYY-MM-DD h:mm A')
        : ''
    },

    gotoReservation (row) {
      this.$router.push({
        path: '/reservations/filter',
        query: {id: row.reservationId}
      })
    },

    markAsBilled (ids) {
      const set = new Set(ids)
      const reservations = this.reservations || []

      reservations.forEach(r => {
        (r.details || []).forEach(d => {
          if (set.has(d.id)) {
            this.$set(d, 'billed', true)
          }
        })
      })
    },

    sendCharges () {
      const billableIds = (this.billableDetails || []).map(d => d.id)

      if (!billableIds.length) return

      this.isSending = true

      const payload = {
        action: 'createTicket',
        reserveesToCharge: billableIds,
        staffId: this.$ftUser.staffId
      }

      this.$ftAPI().put(
        this.addResourceErrorHandler(this.buildCommand(billableIds)),
        payload
      )
    },

    buildCommand (ids) {
      return {
        command: 'chargeDiningReservees',
        loading: this.isLoading,
        params: {children: true},

        afterLoad: r => {
          this.isSending = false

          if (r?.[0]) {
            this.markAsBilled(ids)
          } else {
            console.log('error', r)
          }

          this.normalizeReservations()
        }
      }
    },

    confirmSendCharges () {
      this.$vPrompt({
        title: 'Confirm Billing',
        titleIcon: 'mdi-cash-multiple',
        titleIconColor: 'green',
        text: `Create POS tickets for these ${this.billableReservees} members?`,
        confirmText: 'Send to POS',
        confirmAction: this.sendCharges
      })
    },

    normalizeReservations () {
      (this.reservations || []).forEach(r => {
        (r.details || []).forEach(d => {
          if (d.billed == null) {
            this.$set(d, 'billed', false)
          }
        })
      })
    },

    itemResourceCommands () {
      return [
        {
          command: 'diningReservation',
          load: this.reservations,
          loading: this.isLoading,
          params: {
            children: true,
            include_fuzzy_hash: true,
            order_by: ['diningReservationDetail.reservationId', 'diningReservationDetail.checkNumber', 'diningReservationDetail.show', 'diningReservationDetail.pos'],
            filter: [
              {
                field: 'eventId',
                operator: 'EQUAL',
                values: [this.selectedItemId]
              }
            ],
            include_fields: [
              'diningReservationDetail.eventCostName',
              'diningReservationDetail.eventCostPrice',
              'diningReservationDetail.eventCostChargeCode',
              'diningReservationDetail.memberNumber',
              'diningReservationDetail.guestMemberNumber',
              'diningReservationDetail.guestMemberName',
              'diningReservationDetail.checkNumber',
              'diningReservationDetail.pos',
              'diningReservationDetail.guestTypeName',
              'diningReservationDetail.cancelled',
              'diningReservationDetail.cancelFeeExempt',
              'diningReservationDetail.billed'
            ]
          }
        }
      ]
    }
  }
}
</script>
<style>
.checks-table {
  font-size: 13px;
  border-collapse: separate;
  border-spacing: 0;
}

.checks-table th {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgba(0, 0, 0, 0.55);
  padding: 6px 10px;
  background: #fafafa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.checks-table td {
  padding: 6px 10px;
  vertical-align: middle;
}

.group-row {
  background: #f4f6f8;
  cursor: pointer;
  transition: background 0.15s ease;
}

.group-row:hover {
  background: #e8f2ff;
}

.group-row td {
  padding: 8px 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.resLink {
  color: #1976d2;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: all 0.15s ease;
}

.resLink:hover {
  border-bottom-color: #1976d2;
  opacity: 0.85;
}

.resLink:focus {
  outline: 2px solid rgba(25, 118, 210, 0.35);
  outline-offset: 2px;
}

.group-header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.billed-to {
  margin-left: 24px;
  color: rgba(0, 0, 0, 0.65);
}

.guest-row td {
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  transition: background 0.1s ease;
}

.guest-row:hover td {
  background: rgba(0, 0, 0, 0.015);
}

/* alternating row striping */
.guest-row:nth-child(odd) td {
  background: #fff;
}

.guest-row:nth-child(even) td {
  background: #fcfcfc;
}

.instructions {
  background: #f7faff;
  border-left: 3px solid #90caf9;
  padding: 10px 14px;
  border-radius: 6px;
}

.instructions-title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 2px;
  color: rgba(0, 0, 0, 0.65);
}

.instructions-text {
  font-size: 13px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.75);
}

.eventCounts {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.7);
}

.actions-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;

  padding: 12px 16px;
  background: #fff;

  border-top: 1px solid rgba(0, 0, 0, 0.06);

  position: sticky;
  bottom: 0;
  z-index: 5;

  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
}

.send-charges-btn {
  font-weight: 600;
  letter-spacing: 0.3px;
  padding: 0 18px;
  border-radius: 6px;
  text-transform: none;

  box-shadow: 0 2px 6px rgba(25, 118, 210, 0.2);
  transition: all 0.15s ease;
}

.send-charges-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(25, 118, 210, 0.28);
}
</style>
